import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./EvidenceTable.module.scss";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import { cs } from "date-fns/locale";
import type { EvidenceVolunteer, ActivityLogEntry } from "../../types/evidence";

export interface EvidenceTableProps {
  volunteers: EvidenceVolunteer[];
  onRemoveVolunteer: (volunteerId: string) => void;
  onAddActivity: (volunteerId: string) => void;
  onRemoveActivity: (volunteerId: string, activityId: string) => void;
  onActivityChange: (
    volunteerId: string,
    activityId: string,
    field: keyof ActivityLogEntry,
    value: any
  ) => void;
  onEditActivity: (volunteerId: string, activityId: string) => void;
  onSaveActivity: (volunteerId: string, activityId: string) => void;
  onCancelEditActivity: (volunteerId: string, activityId: string) => void;
  onExportCSV: () => void;
}

export const EvidenceTable = ({
  volunteers,
  onRemoveVolunteer,
  onAddActivity,
  onRemoveActivity,
  onActivityChange,
  onEditActivity,
  onSaveActivity,
  onCancelEditActivity,
  onExportCSV,
}: EvidenceTableProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange = useCallback(
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    },
    []
  );

  const getTotalHours = (activities: ActivityLogEntry[]) => {
    return activities.reduce((sum, activity) => sum + Number(activity.hours), 0);
  };

  return (
    <>
      <div className={styles.toolbar}>
        <h4>Evidence dobrovolníků ({volunteers.length})</h4>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onExportCSV}
          disabled={volunteers.length === 0}
        >
          Export do CSV
        </Button>
      </div>
      <TableContainer className={styles.tableContainer}>
        <Table size="small" className={styles.table}>
          <TableHead className={styles.head}>
            <TableRow>
              <TableCell padding="none" className={styles.cell}>
                Jméno dobrovolníka
              </TableCell>
              <TableCell padding="none" className={styles.cell}>
                Email
              </TableCell>
              <TableCell padding="none" className={styles.cell}>
                Počet aktivit
              </TableCell>
              <TableCell padding="none" className={styles.cell}>
                Celkem hodin
              </TableCell>
              <TableCell padding="none" className={styles.cell}>
                Akce
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={styles.body}>
            {volunteers.map((volunteer) => {
              const volunteerId = volunteer.volunteer_id || volunteer.volunteer_name;
              return (
                <TableRow key={volunteerId} className={styles.accordionRow}>
                  <TableCell colSpan={5} padding="none">
                    <Accordion
                      expanded={expanded === volunteerId}
                      onChange={handleAccordionChange(volunteerId)}
                      className={styles.accordion}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className={styles.accordionSummary}
                      >
                        <Box className={styles.summaryContent}>
                          <Box className={styles.nameCell}>
                            <Avatar
                              alt="avatar"
                              src={volunteer.profile_image}
                              sx={{ width: 32, height: 32 }}
                            >
                              {volunteer.volunteer_name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <div className={styles.nameWrapper}>
                              <p className={styles.name}>{volunteer.volunteer_name}</p>
                            </div>
                          </Box>
                          <Box className={styles.emailCell}>
                            {volunteer.volunteer_email || "—"}
                          </Box>
                          <Box className={styles.countCell}>
                            {volunteer.activities.length}
                          </Box>
                          <Box className={styles.countCell}>
                            {getTotalHours(volunteer.activities).toFixed(2)} h
                          </Box>
                          <Box className={styles.actionCell}>
                            {volunteer.volunteer_id && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/volunteer/${volunteer.volunteer_id}`);
                                }}
                              >
                                Profil
                              </Button>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveVolunteer(volunteerId);
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails className={styles.accordionDetails}>
                        <div className={styles.detailsContent}>
                          <div className={styles.participations}>
                            <div className={styles.participationsHeader}>
                              <h4>Aktivity</h4>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => onAddActivity(volunteerId)}
                              >
                                Přidat aktivitu
                              </Button>
                            </div>
                            {volunteer.activities.length === 0 ? (
                              <p className={styles.noParticipations}>
                                Zatím žádné aktivity
                              </p>
                            ) : (
                              <div className={styles.participationList}>
                                {volunteer.activities.map((activity) => (
                                  <div
                                    key={activity.id}
                                    className={styles.participationItem}
                                  >
                                    {activity.isEditing ? (
                                      <>
                                        <TextField
                                          label="Datum aktivity"
                                          type="date"
                                          value={activity.activity_date}
                                          onChange={(e) =>
                                            onActivityChange(
                                              volunteerId,
                                              activity.id,
                                              "activity_date",
                                              e.target.value
                                            )
                                          }
                                          size="small"
                                          InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                          label="Hodiny"
                                          type="number"
                                          value={activity.hours}
                                          onChange={(e) =>
                                            onActivityChange(
                                              volunteerId,
                                              activity.id,
                                              "hours",
                                              parseFloat(e.target.value)
                                            )
                                          }
                                          size="small"
                                          inputProps={{ min: 0.01, step: 0.25 }}
                                        />
                                        <TextField
                                          label="Popis aktivity"
                                          value={activity.activity_description}
                                          onChange={(e) =>
                                            onActivityChange(
                                              volunteerId,
                                              activity.id,
                                              "activity_description",
                                              e.target.value
                                            )
                                          }
                                          size="small"
                                          fullWidth
                                          inputProps={{ maxLength: 500 }}
                                        />
                                        <TextField
                                          label="Poznámka"
                                          value={activity.note || ""}
                                          onChange={(e) =>
                                            onActivityChange(
                                              volunteerId,
                                              activity.id,
                                              "note",
                                              e.target.value
                                            )
                                          }
                                          size="small"
                                          fullWidth
                                          inputProps={{ maxLength: 500 }}
                                        />
                                        <div className={styles.editActions}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              onSaveActivity(volunteerId, activity.id)
                                            }
                                            color="success"
                                            title="Potvrdit"
                                          >
                                            <CheckIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              onCancelEditActivity(
                                                volunteerId,
                                                activity.id
                                              )
                                            }
                                            color="error"
                                            title="Zrušit"
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className={styles.participationDates}>
                                          <div>
                                            <strong>Datum:</strong>{" "}
                                            {format(
                                              new Date(activity.activity_date),
                                              "d. MMMM yyyy",
                                              { locale: cs }
                                            )}
                                          </div>
                                          <div>
                                            <strong>Hodiny:</strong> {activity.hours} h
                                          </div>
                                          <div>
                                            <strong>Popis:</strong>{" "}
                                            {activity.activity_description}
                                          </div>
                                          {activity.note && (
                                            <div>
                                              <strong>Poznámka:</strong> {activity.note}
                                            </div>
                                          )}
                                        </div>
                                        <div className={styles.viewActions}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              onEditActivity(volunteerId, activity.id)
                                            }
                                            color="primary"
                                            title="Upravit"
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              onRemoveActivity(volunteerId, activity.id)
                                            }
                                            color="error"
                                            title="Smazat"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
